// Columna central del explorer (`.col-table` del mockup): píldoras con los totales del servidor,
// toolbar con conteo y métricas, la tabla de siete columnas y el pie con «Cargar más».
import type { Table } from '@tanstack/react-table';
import { Badge, ErrorState } from '@/kit';
import { cn } from '@/lib/cn';
import { formatShortDateMx } from '@/lib/dateHelpers';
import { formatMXN } from '@/shared/lib/format/money';
import { saleCustomerLabel } from '@/shared/lib/format/saleCustomer';
import { DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import { LoadedScopeFooter, SALES_NOUN, type LoadedScope } from '@/shared/patterns/LoadedScope';
import { SaleTicketProduct, ticketTitle } from '@/shared/patterns/SaleTicket';
import { StatsBar, type StatsBarItem } from '@/shared/patterns/StatsBar';
import type { RecentSale } from '../../api/usePortal';
import { CommissionAwaitingBadge } from '../../components/CommissionAwaitingBadge';
import { SaleStatusBadge } from '../../components/SaleStatusBadge';
import { financierChipOf } from '../../store/lib/storeFormat';
import {
    nextSort,
    saleAmountCents,
    type SalesSegment,
    type SalesStats,
    type SortKey,
    type SortState,
} from '../lib/mySalesFilters';
import {
    financierKeyOf,
    folioTail,
    missingPriceNote,
    PENDING_CELL,
    pluralSales,
} from '../lib/mySalesFormat';
import type { MySalesListErrorCopy, PillCount } from '../lib/mySalesServerQuery';

/** Chip denso de las celdas, a 9px como el `chip text-[9px]` del mock. */
const CELL_CHIP = 'text-[9px]';

/** Las siete columnas del mockup, en su orden. Cliente se queda con el resto del ancho. */
const COLUMNS: DataTableColumn<RecentSale, SortKey>[] = [
    {
        key: 'soldAt',
        label: 'Fecha',
        width: '86px',
        sortKey: 'soldAt',
        cellClassName: () => 'tabular-nums text-[10.5px]',
        // El día de hoy en negrita, como el mockup: es lo que el vendedor busca de un vistazo.
        cell: (sale) => (
            <>
                <span className={sale.today ? 'font-semibold' : 'text-muted-foreground'}>
                    {formatShortDateMx(sale.soldAt)}
                </span>{' '}
                <span className="text-muted-foreground">{sale.atTime}</span>
            </>
        ),
    },
    {
        key: 'saleRef',
        label: 'Venta',
        width: '98px',
        sortKey: 'saleRef',
        cellClassName: () => 'font-mono text-[11px]',
        // El folio entero mide 50 caracteres: va el tramo que distingue la venta y el resto en el title.
        cell: (sale) => <span title={sale.saleRef}>{folioTail(sale.saleRef)}</span>,
    },
    {
        key: 'customerName',
        label: 'Cliente',
        sortKey: 'customerName',
        cellClassName: () => 'font-medium',
        cell: (sale) =>
            saleCustomerLabel(sale) ?? (
                <span className="text-muted-foreground">{PENDING_CELL}</span>
            ),
    },
    {
        key: 'product',
        label: 'Producto',
        width: '124px',
        sortKey: 'product',
        cellClassName: () => 'text-[10.5px] text-muted-foreground',
        cellTitle: (sale) => ticketTitle(sale, sale.product),
        cell: (sale) => <SaleTicketProduct sale={sale} category={null} saleName={sale.product} />,
    },
    {
        key: 'financier',
        label: 'Financ.',
        width: '58px',
        sortKey: 'financier',
        cellOverflow: 'nowrap',
        cell: (sale) => <FinancierBadge sale={sale} />,
    },
    {
        key: 'amount',
        label: 'Monto',
        width: '86px',
        numeric: true,
        sortKey: 'amount',
        cellClassName: () => 'tabular-nums',
        // Lo vendido; sin él, el precio del equipo y luego lo cobrado.
        cell: (sale) => {
            const amountCents = saleAmountCents(sale);
            return amountCents === null ? (
                <span className="text-muted-foreground">{PENDING_CELL}</span>
            ) : (
                formatMXN(amountCents / 100)
            );
        },
    },
    {
        key: 'status',
        label: 'Estado',
        width: '104px',
        sortKey: 'status',
        cellOverflow: 'nowrap',
        cell: (sale) => (
            <span className="inline-flex flex-col items-start gap-0.5">
                <SaleStatusBadge status={sale.status} size="xs" className={CELL_CHIP} />
                <CommissionAwaitingBadge sale={sale} size="xs" className={CELL_CHIP} />
            </span>
        ),
    },
];

/** Las cinco píldoras del mock, en su orden y con el color de su número. */
const SEGMENT_PILLS: { key: SalesSegment; label: string; tone?: StatsBarItem['tone'] }[] = [
    { key: 'all', label: 'Todas' },
    { key: 'today', label: 'Hoy' },
    { key: 'surekeep', label: 'SureKeep', tone: 'success' },
    { key: 'cash', label: 'Contado' },
    { key: 'others', label: 'Otras fin.', tone: 'muted' },
];

export function MySalesTable({
    table,
    pageRows,
    shownCount,
    totalCount,
    scope,
    stats,
    segment,
    pills,
    onSegmentChange,
    loadedNote,
    hasActiveFilters,
    sort,
    onSortChange,
    selectedId,
    onSelect,
    isLoading,
    errorCopy,
    onRetry,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
    nextPageError,
}: {
    table: Table<RecentSale>;
    /** Las filas de la página que se ve, ya filtradas y ordenadas. */
    pageRows: RecentSale[];
    /** Cuántas filas cargadas pasan los filtros. */
    shownCount: number;
    /** Total del servidor para la píldora elegida; `null` si no se puede afirmar. */
    totalCount: number | null;
    scope: LoadedScope;
    stats: SalesStats;
    segment: SalesSegment;
    pills: Record<SalesSegment, PillCount>;
    onSegmentChange: (next: SalesSegment) => void;
    /** Qué mide solo lo cargado mientras queden páginas. */
    loadedNote: string | null;
    /** Discrimina el «no hay ventas» del «los filtros no dejaron ninguna». */
    hasActiveFilters: boolean;
    sort: SortState;
    onSortChange: (next: SortState) => void;
    selectedId: string | null;
    onSelect: (saleId: string) => void;
    isLoading: boolean;
    /** La primera página no llegó: el motivo en lugar de la tabla. */
    errorCopy: MySalesListErrorCopy | null;
    onRetry: () => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    onLoadMore: () => void;
    /** Por qué falló la última página pedida; lo cargado sigue en la tabla. */
    nextPageError: string | null;
}) {
    // Una lectura fallida NO se pinta como tabla vacía: el vendedor concluiría que no vendió nada.
    if (errorCopy) {
        return (
            <ErrorState
                className="m-3"
                title={errorCopy.title}
                description={errorCopy.description}
                onRetry={errorCopy.canRetry ? onRetry : undefined}
            />
        );
    }

    return (
        <>
            <StatsBar
                items={SEGMENT_PILLS.map((pill) => ({
                    ...pill,
                    value: isLoading ? PENDING_CELL : pills[pill.key].value,
                    title: isLoading ? undefined : pills[pill.key].title,
                }))}
                active={segment}
                onChange={onSegmentChange}
                ariaLabel="Recortes de tus ventas"
                className="min-h-10 bg-transparent px-2"
            />

            {loadedNote === null || isLoading ? null : (
                <p
                    role="status"
                    className="flex-none border-b border-border bg-muted/40 px-3 py-1.5 text-[10px] leading-snug text-muted-foreground"
                >
                    {loadedNote}
                </p>
            )}

            {/* .table-toolbar */}
            <div className="flex flex-none flex-wrap items-center gap-2 border-b border-border px-3 py-2">
                <div className="text-[11px] text-muted-foreground">
                    {isLoading ? `${PENDING_CELL} ventas` : pluralSales(totalCount ?? shownCount)}
                    {isFetchingNextPage ? ' · cargando más…' : ''}
                </div>
                <StatMetric
                    label="Ticket prom"
                    value={
                        stats.avgTicketCents === null ? null : formatMXN(stats.avgTicketCents / 100)
                    }
                />
                <StatMetric
                    label="Volumen"
                    value={stats.volumeCents === null ? null : formatMXN(stats.volumeCents / 100)}
                />
                <StatMetric
                    label="% SK"
                    value={
                        stats.surekeepPercent === null ? null : `${String(stats.surekeepPercent)}%`
                    }
                    className="text-success"
                />
            </div>

            {/* Sin `minWidth`: el mockup deja que las columnas se compriman antes que esconder las
                últimas detrás de un scroll horizontal. */}
            <DataTable
                columns={COLUMNS}
                rows={pageRows}
                rowKey={(sale) => sale.saleId}
                density="compact"
                sort={sort}
                onSortToggle={(key) => {
                    onSortChange(nextSort(sort, key));
                }}
                onRowClick={(sale) => {
                    onSelect(sale.saleId);
                }}
                isRowSelected={(sale) => sale.saleId === selectedId}
                isLoading={isLoading}
                empty={emptyNote({ hasActiveFilters, hasNextPage })}
            />

            {/* Las ventas sin monto quedaron fuera del volumen: callarlo dejaría un promedio que no
                cuadra con las filas que se ven. */}
            {stats.missingPriceCount > 0 ? (
                <div className="flex-none border-t border-border px-3 py-1.5 text-[10px] leading-snug text-muted-foreground">
                    {missingPriceNote(stats.missingPriceCount)}
                </div>
            ) : null}

            <LoadedScopeFooter
                noun={SALES_NOUN}
                scope={isLoading ? null : scope}
                shown={isLoading ? null : shownCount}
                table={table}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                onLoadMore={onLoadMore}
                nextPageError={nextPageError}
            />
        </>
    );
}

/** Sin filas: culpar a los filtros sin filtros puestos deja al vendedor buscando cuál quitar. */
function emptyNote({
    hasActiveFilters,
    hasNextPage,
}: {
    hasActiveFilters: boolean;
    hasNextPage: boolean;
}) {
    if (!hasActiveFilters) return 'Todavía no tienes ventas registradas.';
    if (hasNextPage) {
        return 'Ninguna venta cargada coincide con los filtros; usa «Cargar más» para traer el resto.';
    }
    return 'Ninguna venta coincide con los filtros.';
}

/** Métrica del toolbar. Sin nada que medir va en guion: un cero afirmaría que se vendió $0. */
function StatMetric({
    label,
    value,
    className,
}: {
    label: string;
    value: string | null;
    className?: string;
}) {
    return (
        <span className="inline-flex items-baseline gap-1 whitespace-nowrap rounded bg-muted/40 px-1.5 py-0.5">
            <span className="text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground">
                {label}
            </span>
            <span className={cn('text-[10.5px] font-bold tabular-nums', className)}>
                {value ?? PENDING_CELL}
            </span>
        </span>
    );
}

/** Chip corto de la financiera («SK», «PJ», «CTD»), tal cual el `chipFin` del mock. */
function FinancierBadge({ sale }: { sale: RecentSale }) {
    // El listado se castea sin validar: sin financiera determinable va un neutro, porque «CTD»
    // afirmaría un contado que nadie leyó.
    const key = financierKeyOf(sale.type, sale.externalProvider);
    if (key === null) return <span className="text-muted-foreground">{PENDING_CELL}</span>;
    const chip = financierChipOf(sale.type, sale.externalProvider);
    return (
        <Badge variant={chip.variant} size="xs" className={CELL_CHIP}>
            {chip.label}
        </Badge>
    );
}
